// frontend/src/utils/imageCompression.js

/**
 * Compresses and resizes an image file using HTML5 Canvas.
 * 
 * @param {File} file - The original image file.
 * @param {number} quality - Compression quality (0.1 to 1.0). Default 0.4 (40%).
 * @param {number} maxWidth - Maximum width. Default 1920px.
 * @param {number} maxHeight - Maximum height. Default 1080px.
 * @returns {Promise<File>} - A promise that resolves to the compressed File object.
 */
export const compressImage = (file, quality = 0.4, maxWidth = 1920, maxHeight = 1080) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
  
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
  
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let targetWidth = img.width;
          let targetHeight = img.height;
  
          // Calculate new dimensions while maintaining aspect ratio
          if (img.width > maxWidth || img.height > maxHeight) {
            const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
            targetWidth = Math.round(img.width * ratio);
            targetHeight = Math.round(img.height * ratio);
          }
  
          canvas.width = targetWidth;
          canvas.height = targetHeight;
  
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  
          // Determine output format
          let outputFormat = 'image/jpeg';
          if (file.type === 'image/png') {
            outputFormat = 'image/png';
          } else if (file.type === 'image/webp') {
            outputFormat = 'image/webp';
          }
  
          // Compress
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Create a new file object with the compressed blob
                const compressedFile = new File([blob], file.name, {
                  type: outputFormat,
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Image compression failed'));
              }
            },
            outputFormat,
            quality // 0.4 = 40% quality
          );
        };
  
        img.onerror = (err) => {
          reject(err);
        };
      };
  
      reader.onerror = (err) => {
        reject(err);
      };
    });
  };